import React, { useMemo } from "react";

import {
  FieldSelectFit,
  TRADE_LOG_FILTER_INPUT_CLASS,
  type TradeFilterSelectOption,
} from "@/components/trade-filter-selects";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { ReportStrategyResponse, ReportTypeOption } from "../../../types/reports";

const ALL_FILTER_VALUE = "all";

export interface SavedReportsActionBarProps {
  brokerAccountFilterCode: string;
  brokerAccountOptions: Array<{ value: string; label: string }>;
  brokerAccountsLoading: boolean;
  onBrokerAccountFilterChange: (brokerAccountCode: string) => void;

  strategyFilterId: string;
  strategyOptions: ReportStrategyResponse[];
  strategiesLoading: boolean;
  onStrategyFilterChange: (strategyId: string) => void;

  reportTypeFilterKey: string;
  reportTypeOptions: ReportTypeOption[];
  reportTypesLoading: boolean;
  onReportTypeFilterChange: (reportTypeKey: string) => void;

  searchQuery: string;
  onSearchQueryChange: (query: string) => void;

  /** Hidden in board view — types are shown as columns instead. */
  hideReportTypeFilter?: boolean;
}

function toFilterValue(value: string): string {
  return value.trim() ? value : ALL_FILTER_VALUE;
}

function fromFilterValue(value: string): string {
  return value === ALL_FILTER_VALUE ? "" : value;
}

const SavedReportsActionBar: React.FC<SavedReportsActionBarProps> = ({
  brokerAccountFilterCode,
  brokerAccountOptions,
  brokerAccountsLoading,
  onBrokerAccountFilterChange,
  strategyFilterId,
  strategyOptions,
  strategiesLoading,
  onStrategyFilterChange,
  reportTypeFilterKey,
  reportTypeOptions,
  reportTypesLoading,
  onReportTypeFilterChange,
  searchQuery,
  onSearchQueryChange,
  hideReportTypeFilter = false,
}) => {
  const brokerAccountSelectOptions = useMemo<TradeFilterSelectOption[]>(
    () => [
      { value: ALL_FILTER_VALUE, label: "All accounts" },
      ...brokerAccountOptions.map((opt) => ({
        value: opt.value,
        label: opt.label,
      })),
    ],
    [brokerAccountOptions],
  );

  const reportTypeSelectOptions = useMemo<TradeFilterSelectOption[]>(
    () => [
      { value: ALL_FILTER_VALUE, label: "All report types" },
      ...reportTypeOptions.map((opt) => ({
        value: opt.key,
        label: opt.label,
      })),
    ],
    [reportTypeOptions],
  );

  const strategySelectOptions = useMemo<TradeFilterSelectOption[]>(
    () => [
      { value: ALL_FILTER_VALUE, label: "All strategies" },
      ...strategyOptions.map((opt) => ({
        value: opt.id,
        label: opt.name,
      })),
    ],
    [strategyOptions],
  );

  return (
    <div data-reports-action-bar="" className="flex min-w-0 flex-1 items-center">
      <div
        data-reports-filter-bar=""
        className="flex min-w-0 flex-1 items-center gap-3"
      >
        <div
          data-reports-filter-bar-filters=""
          className="flex min-w-0 items-center gap-3"
        >
            <FieldSelectFit
              filterChrome="reports"
              value={toFilterValue(brokerAccountFilterCode)}
              onValueChange={(value) =>
                onBrokerAccountFilterChange(fromFilterValue(value))
              }
              options={brokerAccountSelectOptions}
              disabled={brokerAccountsLoading}
            />
            {!hideReportTypeFilter ? (
              <FieldSelectFit
                filterChrome="reports"
                value={toFilterValue(reportTypeFilterKey)}
                onValueChange={(value) =>
                  onReportTypeFilterChange(fromFilterValue(value))
                }
                options={reportTypeSelectOptions}
                disabled={reportTypesLoading}
              />
            ) : null}
            <FieldSelectFit
              filterChrome="reports"
              value={toFilterValue(strategyFilterId)}
              onValueChange={(value) =>
                onStrategyFilterChange(fromFilterValue(value))
              }
              options={strategySelectOptions}
              disabled={strategiesLoading}
            />
          </div>
          <Input
            value={searchQuery}
            onChange={(e) => onSearchQueryChange(e.target.value)}
            placeholder="Search reports…"
            data-reports-table-search=""
            className={cn(
              TRADE_LOG_FILTER_INPUT_CLASS,
              "h-8 min-w-[12rem] max-w-[18rem] shrink-0 normal-case tracking-normal",
            )}
            aria-label="Search reports"
          />
        </div>
    </div>
  );
};

export default SavedReportsActionBar;
